import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { XdsButtonComponent, XdsDialogShellComponent, XdsDialogFooterDirective } from '../../../../shared/ui';
import { CompanyInfo } from '../../services/auth/response/authResponseDto.model';

export interface CompanySelectionDialogData {
  companies: CompanyInfo[];
}

/**
 * Dialog zur Auswahl der Company nach Login bei Multi-Company Users.
 *
 * Wird angezeigt wenn:
 * - User hat mehrere Companies
 * - Keine Default-Company gesetzt ist
 * - User hat keinen Company-ID beim Login angegeben
 *
 * @example
 * const dialogRef = this.dialog.open(CompanySelectionDialogComponent, {
 *   data: { companies: availableCompanies },
 * });
 * dialogRef.afterClosed().subscribe((companyId?: number) => { ... });
 */
@Component({
  selector: 'app-company-selection-dialog',
  standalone: true,
  imports: [CommonModule, TranslateModule, XdsButtonComponent, XdsDialogShellComponent, XdsDialogFooterDirective],
  templateUrl: './company-selection-dialog.component.html',
  styleUrls: ['./company-selection-dialog.component.css']
})
export class CompanySelectionDialogComponent implements OnInit {
  /** Aktuell ausgewählte Company (für Highlighting) */
  selectedCompanyId: number | null = null;

  /** Ist gerade ein Laden aktiv? */
  isLoading: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<CompanySelectionDialogComponent, number | undefined>,
    @Inject(MAT_DIALOG_DATA) public data: CompanySelectionDialogData
  ) {}

  ngOnInit(): void {
    // Wenn eine Default-Company existiert, diese vorauswählen
    const defaultCompany = this.data.companies.find(c => c.IsDefault);
    if (defaultCompany) {
      this.selectedCompanyId = defaultCompany.CompanyId;
    } else if (this.data.companies.length > 0) {
      // Sonst die erste Company vorauswählen
      this.selectedCompanyId = this.data.companies[0].CompanyId;
    }
  }

  /**
   * Company-Karte wurde angeklickt
   */
  selectCompany(companyId: number): void {
    this.selectedCompanyId = companyId;
  }

  /**
   * "Weiter" Button wurde geklickt
   */
  confirmSelection(): void {
    if (this.selectedCompanyId) {
      this.isLoading = true;
      this.dialogRef.close(this.selectedCompanyId);
    }
  }

  /**
   * Dialog schließen ohne Auswahl
   */
  cancel(): void {
    this.dialogRef.close(undefined);
  }

  /**
   * Gibt die Rolle als lesbaren String zurück
   */
  getRoleLabel(role: number): string {
    switch (role) {
      case 1: return 'Manager';
      case 2: return 'Mitarbeiter';
      case 3: return 'Teamleiter';
      default: return 'Mitarbeiter';
    }
  }

  /**
   * Gibt das Icon für die Rolle zurück
   */
  getRoleIcon(role: number): string {
    switch (role) {
      case 1: return 'fa-user-tie';
      case 2: return 'fa-user';
      case 3: return 'fa-users-gear';
      default: return 'fa-user';
    }
  }
}
